<template>
  <DialogComponent
    ref="modal"
    type="modal"
    mainClass="fixed z-[61]"
    @closed="resetForm"
  >
    <div class="py-2">
      <div class="text-xl font-bold mb-5 text-primary">Retrait de fonds</div>

      <div v-if="step === 'network'" class="space-y-4">
        <p class="text-sm text-gray-600 mb-4">Sélectionnez votre réseau de paiement :</p>
        <div
          v-for="net in networks"
          :key="net.id"
          @click="selectNetwork(net.id)"
          class="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 hover:bg-gray-100 cursor-pointer transition-all border-2"
          :class="form.network === net.id ? 'border-primary bg-primary/5' : 'border-transparent'"
        >
          <div class="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm">
            <img :src="net.logo" :alt="net.name" class="w-8 h-8 object-contain" />
          </div>
          <div class="flex-1 font-bold">{{ net.name }}</div>
          <div v-if="form.network === net.id" class="w-6 h-6 text-primary">
            <ColorImg src="/images/all/check.svg" class="w-full h-full bg-current" />
          </div>
        </div>
      </div>

      <div v-else-if="step === 'details'" class="space-y-5">
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Numéro de téléphone
          </label>
          <div class="relative">
            <input
              v-model="form.phoneNumber"
              type="tel"
              placeholder="Ex: 0196743913"
              class="w-full px-4 py-3 rounded-xl border-2 border-gray-100 focus:border-primary focus:outline-none transition-all"
            />
          </div>
        </div>

        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Montant à retirer (CFA)
          </label>
          <div class="relative">
            <input
              v-model.number="form.amount"
              type="number"
              placeholder="Ex: 5000"
              class="w-full px-4 py-3 rounded-xl border-2 border-gray-100 focus:border-primary focus:outline-none transition-all font-bold text-lg"
            />
          </div>
          <p class="text-[10px] text-gray-400 mt-2">
            * Des frais fixes de 150 FCFA seront déduits du montant total.
          </p>
        </div>

        <div v-if="form.amount >= 500" class="bg-primary/5 p-4 rounded-xl border border-primary/10">
          <div class="flex justify-between text-sm mb-1">
            <span class="text-gray-600">Montant brut :</span>
            <span class="font-semibold">{{ form.amount }} CFA</span>
          </div>
          <div class="flex justify-between text-sm mb-1 text-red-500">
            <span>Frais :</span>
            <span>- 150 CFA</span>
          </div>
          <div class="flex justify-between text-base font-bold border-t border-primary/20 pt-2 mt-2 text-primary">
            <span>Montant Net :</span>
            <span>{{ Math.max(0, form.amount - 150) }} CFA</span>
          </div>
        </div>
      </div>

      <div v-else-if="step === 'processing'" class="py-10 text-center">
        <div class="flex justify-center mb-6">
          <div class="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
        <h3 class="text-lg font-bold mb-2">Traitement en cours...</h3>
        <p class="text-sm text-gray-500">Veuillez patienter pendant que nous initions votre retrait.</p>
      </div>

      <div v-else-if="step === 'success'" class="py-6 text-center">
        <div class="flex justify-center mb-6">
          <div class="w-20 h-20 bg-green-100 text-green-500 rounded-full flex items-center justify-center">
            <ColorImg src="/images/all/check.svg" class="w-12 h-12 bg-current" />
          </div>
        </div>
        <h3 class="text-xl font-bold mb-2 text-green-600">Retrait Initié !</h3>
        <p class="text-sm text-gray-500 mb-2">
          Votre demande de retrait de <span class="font-bold text-gray-800">{{ form.amount }} CFA</span> a été envoyée avec succès.
        </p>
        <p v-if="withdrawalData?.id" class="text-[10px] text-gray-400 mb-6">
          Référence : {{ withdrawalData.id }}
        </p>
        <button
          @click="close"
          class="w-full py-4 rounded-2xl bg-gray-100 font-bold hover:bg-gray-200 transition-all"
        >
          Fermer
        </button>
      </div>

      <div v-else-if="step === 'error'" class="py-6 text-center">
        <div class="flex justify-center mb-6">
          <div class="w-20 h-20 bg-red-100 text-red-500 rounded-full flex items-center justify-center">
            <ColorImg src="/images/all/close.svg" class="w-12 h-12 bg-current" />
          </div>
        </div>
        <h3 class="text-xl font-bold mb-2 text-red-600">Échec du retrait</h3>
        <p class="text-sm text-gray-500 mb-6">
          {{ errorMessage || "Une erreur est survenue lors de l'initiation de votre retrait. Veuillez réessayer." }}
        </p>
        <div class="flex gap-4">
          <button
            @click="step = 'details'"
            class="flex-1 py-4 rounded-2xl bg-primary text-white font-bold hover:opacity-90 transition-all"
          >
            Réessayer
          </button>
          <button
            @click="close"
            class="flex-1 py-4 rounded-2xl bg-gray-100 font-bold hover:bg-gray-200 transition-all"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>

    <div v-if="step === 'network' || step === 'details'" class="flex gap-4 mt-8">
      <button
        v-if="step === 'details'"
        @click="step = 'network'"
        class="flex-1 py-4 rounded-2xl bg-gray-100 font-bold text-gray-600 hover:bg-gray-200 transition-all"
      >
        Retour
      </button>
      <button
        v-else
        @click="close"
        class="flex-1 py-4 rounded-2xl bg-gray-100 font-bold text-gray-600 hover:bg-gray-200 transition-all"
      >
        Annuler
      </button>
      
      <button
        @click="nextStep"
        :disabled="!isStepValid"
        class="flex-[2] py-4 rounded-2xl bg-primary text-white font-bold shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:grayscale disabled:scale-100"
      >
        {{ step === 'network' ? 'Suivant' : 'Confirmer le retrait' }}
      </button>
    </div>
  </DialogComponent>

  <VBusHelper
    :reactOn="'WALLET.WITHDRAW_MODAL.SHOW'"
    :handler="show"
  />
</template>

<script setup>
import { ref, computed, getCurrentInstance, onMounted, onBeforeUnmount } from 'vue';

const { proxy } = getCurrentInstance();
const { $bus, $constants } = proxy;

const step = ref('network'); // network, details, processing, success, error
const errorMessage = ref('');
const withdrawalData = ref(null);

const form = ref({
  network: '',
  phoneNumber: '',
  amount: 0
});

const networks = [
  { id: 'mtn', name: 'MTN Mobile Money', logo: '/images/networks/mtn.png' },
  { id: 'moov', name: 'Moov Money', logo: '/images/networks/moov.png' }
];

const isStepValid = computed(() => {
  if (step.value === 'network') return !!form.value.network;
  if (step.value === 'details') {
    return form.value.phoneNumber.length >= 8 && form.value.amount >= 500;
  }
  return false;
});

const show = () => {
  step.value = 'network';
  proxy.$refs.modal.show();
};

const close = () => {
  proxy.$refs.modal.hide();
};

const selectNetwork = (id) => {
  form.value.network = id;
};

const nextStep = () => {
  if (step.value === 'network') {
    step.value = 'details';
  } else if (step.value === 'details') {
    submitWithdrawal();
  }
};

const submitWithdrawal = () => {
  $bus.emit($constants.EVENTS.WITHDRAW.REQUEST, {
    amount: form.value.amount,
    phoneNumber: form.value.phoneNumber,
    network: form.value.network
  });
};

const resetForm = () => {
  step.value = 'network';
  errorMessage.value = '';
  withdrawalData.value = null;
  form.value = {
    network: '',
    phoneNumber: '',
    amount: 0
  };
};

// Event Handlers
const onProcessing = () => { step.value = 'processing'; };
const onSuccess = (data) => { 
  withdrawalData.value = data;
  step.value = 'success'; 
};
const onError = (msg) => {
  errorMessage.value = msg;
  step.value = 'error';
};

onMounted(() => {
  $bus.on($constants.EVENTS.WITHDRAW.PROCESSING, onProcessing);
  $bus.on($constants.EVENTS.WITHDRAW.SUCCESS, onSuccess);
  $bus.on($constants.EVENTS.WITHDRAW.ERROR, onError);
});

onBeforeUnmount(() => {
  $bus.off($constants.EVENTS.WITHDRAW.PROCESSING, onProcessing);
  $bus.off($constants.EVENTS.WITHDRAW.SUCCESS, onSuccess);
  $bus.off($constants.EVENTS.WITHDRAW.ERROR, onError);
});
</script>
